import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { 
  CheckCircle, FileText, Shield, ArrowRight, 
  Mail, Building2, Phone
} from 'lucide-react';

const AVIDA_LOGO = "https://customer-assets.emergentagent.com/job_packet-digitizer/artifacts/f0t7kezs_Avida%20Logo%20Full%20Color.png";

export default function EmploymentSuccessPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const formType = location.state?.formType || 'application';
  
  const isApplication = formType === 'application';
  const isBackgroundCheck = formType === 'background-check';

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex flex-col">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <img src={AVIDA_LOGO} alt="Avida" className="h-10" />
          <span className="text-slate-500 text-sm">Application Submitted</span>
        </div>
      </header>

      {/* Success Content */}
      <main className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-2xl">
          <Card className="p-8 md:p-12 text-center bg-white shadow-xl" data-testid="success-card">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            
            <h1 className="text-2xl md:text-3xl font-bold text-slate-800 mb-3" data-testid="success-title">
              {isApplication && 'Application Submitted Successfully!'}
              {isBackgroundCheck && 'Background Check Form Submitted!'}
              {!isApplication && !isBackgroundCheck && 'Form Submitted Successfully!'}
            </h1>
            
            <p className="text-lg text-slate-600 mb-8">
              {isApplication && 'Thank you for your interest in joining Avida Personal Care.'}
              {isBackgroundCheck && 'Your Background Information Disclosure has been received.'}
            </p>

            {/* What's Next */}
            <Card className="bg-slate-50 p-6 text-left mb-8">
              <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                <Mail className="w-5 h-5 text-amber-500" />
                What Happens Next?
              </h3>
              <ul className="space-y-3 text-sm text-slate-600">
                <li className="flex items-start gap-2">
                  <span className="w-6 h-6 bg-amber-100 text-amber-600 rounded-full flex items-center justify-center text-xs font-bold shrink-0">1</span>
                  <span>You'll receive a confirmation email with a copy of your submission</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-6 h-6 bg-amber-100 text-amber-600 rounded-full flex items-center justify-center text-xs font-bold shrink-0">2</span>
                  <span>Our HR team will review your {isApplication ? 'application' : 'form'} within 3-5 business days</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-6 h-6 bg-amber-100 text-amber-600 rounded-full flex items-center justify-center text-xs font-bold shrink-0">3</span>
                  <span>
                    {isApplication 
                      ? "If selected, you'll be contacted for an interview"
                      : 'The background check process will be initiated'}
                  </span>
                </li>
              </ul>
            </Card>

            {/* Additional Forms Prompt */}
            {isApplication && (
              <Card className="bg-blue-50 border-blue-200 p-6 text-left mb-8">
                <h3 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  Complete Your Background Check
                </h3>
                <p className="text-sm text-blue-700 mb-4">
                  Wisconsin law requires all personal care workers to complete the Background Information Disclosure (BID) form.
                </p>
                <Button 
                  data-testid="start-bid-btn"
                  onClick={() => navigate('/employment/background-check')}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  Start Background Check Form <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Card>
            )}

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button 
                variant="outline" 
                data-testid="back-to-portal-btn"
                onClick={() => navigate('/employment')}
              >
                <FileText className="w-4 h-4 mr-2" />
                Back to Employment Portal
              </Button>
              <Button 
                variant="outline"
                data-testid="back-to-home-btn"
                onClick={() => navigate('/')}
              >
                Return to Home
              </Button>
            </div>
          </Card>

          {/* Contact Info */}
          <div className="mt-8 text-center text-slate-500 text-sm">
            <p className="mb-2">Questions? Contact our HR department:</p>
            <div className="flex items-center justify-center gap-4">
              <span className="flex items-center gap-1">
                <Phone className="w-4 h-4" /> (414) 207-4860
              </span>
              <span className="flex items-center gap-1">
                <Mail className="w-4 h-4" /> hr@avidahealth.org
              </span>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white/50 border-t py-4">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="flex items-center justify-center gap-2">
            <Building2 className="w-4 h-4 text-slate-400" />
            <span className="text-slate-500 text-sm">Avida Personal Care</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
